import React, { useState, useEffect, useCallback } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Alert, AlertTitle, AlertDescription } from './ui/alert';
import { Wrench, Loader2, Download, X, AlertTriangle, CheckCircle2, Trash2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Stock injector / fuel pressure presets for common BMW + aftermarket combos.
// User can override, these just save typing for the most common cases.
const PRESETS = {
  injector_cc: { stock_cc: 395 },          // N51 / N52 factory Bosch
  fuel_pressure_psi: { stock_psi: 58 },    // BMW N-series factory regulated
};

const getAuthHeaders = () => {
  const token = (() => { try { return localStorage.getItem('auth_token'); } catch { return null; } })();
  return token ? { Authorization: `Bearer ${token}` } : {};
};

/**
 * DeriveTuneDialog — generate a new tune from an existing one by applying
 * hardware-mod scaling (bigger injectors, different fuel pressure, etc.).
 *
 * Backend: POST /api/tunes/derive
 * Discovers available mods via GET /api/tunes/derive/mods so new mods added
 * on the backend automatically show up here.
 */
export default function DeriveTuneDialog({ open, onClose, versions, onDone, prefill }) {
  const [availableMods, setAvailableMods] = useState([]);
  const [baseFilename, setBaseFilename] = useState('');
  const [selectedMods, setSelectedMods] = useState([]);  // [{id, params:{...}}]
  const [building, setBuilding] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return;
    fetch(`${API_URL}/api/tunes/derive/mods`)
      .then(r => r.ok ? r.json() : { mods: [] })
      .then(d => setAvailableMods(d.mods || []))
      .catch(() => setAvailableMods([]));
  }, [open]);

  // Apply incoming prefill (e.g. from the scorecard's "Generate Corrected
  // Tune" button) — sets the base tune and seeds the mod list. Runs once
  // per open so user edits aren't clobbered on re-render.
  useEffect(() => {
    if (!open) return;
    if (prefill) {
      if (prefill.base_filename) setBaseFilename(prefill.base_filename);
      if (Array.isArray(prefill.mods) && prefill.mods.length) {
        setSelectedMods(prefill.mods.map(m => ({
          id: m.id,
          params: Object.fromEntries(
            Object.entries(m.params || {}).map(([k, v]) => [k, String(v)])
          ),
        })));
      }
      return;
    }
    // Default base = V11 if present, else first non-stock version.
    if (Array.isArray(versions) && versions.length && !baseFilename) {
      const pref = versions.find(v => v.filename?.includes('V11'))
        || versions.find(v => v.filename?.endsWith('.bin'));
      if (pref?.filename) setBaseFilename(pref.filename);
    }
  }, [open, prefill, versions, baseFilename]);

  const addMod = useCallback((modId) => {
    const preset = PRESETS[modId] || {};
    // Store all params as strings so the input never fights the user. We
    // convert to numbers only at submit time.
    const params = {};
    Object.entries(preset).forEach(([k, v]) => { params[k] = String(v); });
    setSelectedMods(prev => [...prev, { id: modId, params }]);
  }, []);

  const removeMod = useCallback((idx) => {
    setSelectedMods(prev => prev.filter((_, i) => i !== idx));
  }, []);

  const updateParam = useCallback((idx, key, value) => {
    // Keep raw string — user can type "5.5", clear fully, paste, backspace,
    // etc., without each keystroke being coerced to a Number (which silently
    // drops trailing dots and feels like the box is rejecting input).
    setSelectedMods(prev => prev.map((m, i) =>
      i === idx ? { ...m, params: { ...m.params, [key]: value } } : m
    ));
  }, []);

  const canBuild = baseFilename && selectedMods.length > 0 && selectedMods.every(m =>
    Object.values(m.params).every(v => {
      const n = parseFloat(v);
      return Number.isFinite(n) && n > 0;
    })
  );

  const build = useCallback(async () => {
    setBuilding(true);
    setError(null);
    setResult(null);
    // Convert string params → numbers at submit time.
    const modsPayload = selectedMods.map(m => ({
      id: m.id,
      params: Object.fromEntries(
        Object.entries(m.params).map(([k, v]) => [k, parseFloat(v)])
      ),
    }));
    try {
      const res = await fetch(`${API_URL}/api/tunes/derive`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ base_filename: baseFilename, mods: modsPayload }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        const detail = body?.detail;
        const msg = typeof detail === 'string' ? detail
          : detail?.error || detail?.violations?.join(' · ')
          || `HTTP ${res.status}`;
        setError(msg);
      } else {
        setResult(body);
        if (onDone) onDone();
      }
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setBuilding(false);
    }
  }, [baseFilename, selectedMods, onDone]);

  const reset = () => {
    setSelectedMods([]);
    setResult(null);
    setError(null);
  };

  if (!open) return null;

  const baseOptions = (versions || []).filter(v => v.filename?.endsWith('.bin'));

  return (
    <div
      className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid="derive-tune-dialog"
      onClick={onClose}
    >
      <div
        className="bg-[#0A0A0A] border border-[#27272A] rounded-sm w-full max-w-2xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-[#27272A]">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-sm bg-gradient-to-br from-[#FF5E1A] to-amber-500 flex items-center justify-center">
              <Wrench className="w-4 h-4 text-white" />
            </div>
            <div>
              <h2 className="text-white font-['Outfit'] font-bold text-base">Derive Tune from Existing</h2>
              <p className="text-[11px] text-zinc-500">
                Re-scale fuel maps for new hardware — bigger injectors, different pressure
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-zinc-500 hover:text-white p-1"
            data-testid="derive-close-btn"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="p-5 space-y-5">
          {/* Base tune picker */}
          <div>
            <Label className="text-zinc-400 text-xs uppercase tracking-wider mb-2 block">
              1 · Base Tune
            </Label>
            <select
              value={baseFilename}
              onChange={(e) => setBaseFilename(e.target.value)}
              className="w-full bg-[#050505] border border-[#27272A] text-white text-sm rounded-sm p-2.5 font-['JetBrains_Mono']"
              data-testid="derive-base-select"
            >
              <option value="">— Select a base tune —</option>
              {baseOptions.map(v => (
                <option key={v.filename} value={v.filename}>
                  {v.filename} {v.version ? `(${v.version})` : ''}
                </option>
              ))}
            </select>
            <p className="text-[10px] text-zinc-600 mt-1">
              Fuel maps inside this tune will be scaled to match your new hardware.
            </p>
          </div>

          {/* Mods list */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="text-zinc-400 text-xs uppercase tracking-wider">
                2 · Hardware Mods
              </Label>
              <div className="flex gap-2">
                {availableMods.map(m => (
                  <Button
                    key={m.id}
                    size="sm"
                    variant="outline"
                    onClick={() => addMod(m.id)}
                    className="border-[#FF5E1A]/30 text-[#FF5E1A] hover:bg-[#FF5E1A]/10 rounded-sm text-[10px] h-7 px-2.5"
                    data-testid={`derive-add-mod-${m.id}`}
                  >
                    + {m.label}
                  </Button>
                ))}
              </div>
            </div>

            {selectedMods.length === 0 && (
              <div className="p-4 bg-[#050505] border border-dashed border-[#27272A] rounded-sm text-center">
                <p className="text-xs text-zinc-500">
                  Add a mod above — e.g. "Injector size swap" for your 550cc install.
                </p>
              </div>
            )}

            <div className="space-y-3">
              {selectedMods.map((m, idx) => {
                const spec = availableMods.find(a => a.id === m.id);
                if (!spec) return null;
                return (
                  <div key={`${m.id}-${idx}`} className="p-3 bg-[#050505] border border-[#27272A] rounded-sm" data-testid={`derive-mod-row-${idx}`}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-white font-['Outfit'] font-medium">{spec.label}</span>
                      <button
                        onClick={() => removeMod(idx)}
                        className="text-zinc-600 hover:text-red-400"
                        aria-label="Remove mod"
                        data-testid={`derive-remove-mod-${idx}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      {Object.entries(spec.params).map(([key, hint]) => (
                        <div key={key}>
                          <Label className="text-[10px] text-zinc-500 uppercase tracking-wider mb-1 block">
                            {key.replace(/_/g, ' ')}
                          </Label>
                          <Input
                            type="text"
                            inputMode="decimal"
                            value={m.params[key] ?? ''}
                            onChange={(e) => updateParam(idx, key, e.target.value)}
                            placeholder={key.includes('cc') ? 'e.g. 550' : 'e.g. 58'}
                            className="bg-[#0A0A0A] border-[#27272A] text-white text-sm rounded-sm font-['JetBrains_Mono']"
                            data-testid={`derive-param-${idx}-${key}`}
                          />
                          <p className="text-[9px] text-zinc-600 mt-1">{hint}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Error / Result */}
          {error && (
            <Alert className="bg-red-500/10 border-red-500/30 rounded-sm" data-testid="derive-error">
              <AlertTriangle className="h-4 w-4 text-red-400" />
              <AlertTitle className="text-white font-['Outfit']">Build failed</AlertTitle>
              <AlertDescription className="text-zinc-400 text-xs">{error}</AlertDescription>
            </Alert>
          )}

          {result && (
            <Alert className="bg-emerald-500/10 border-emerald-500/30 rounded-sm" data-testid="derive-success">
              <CheckCircle2 className="h-4 w-4 text-emerald-400" />
              <AlertTitle className="text-white font-['Outfit']">Derived tune ready</AlertTitle>
              <AlertDescription className="text-zinc-400 text-xs space-y-2">
                <div className="font-['JetBrains_Mono'] text-[11px] text-zinc-300">
                  {result.clean_filename}
                </div>
                <div className="flex gap-3 text-[11px]">
                  <span>
                    Fuel × <span className="text-white font-bold">{result.combined_fuel_scale?.toFixed(4)}</span>
                  </span>
                  <span>
                    {result.main_fuel_cells_changed + result.load_fuel_cells_changed} cells modified
                  </span>
                  <span>
                    {result.total_bytes_changed_vs_base} bytes changed
                  </span>
                </div>
                <p className="text-zinc-500 text-[10px]">
                  {result.predicted_outcomes?.rationale}
                </p>
                <div className="flex gap-2 pt-1">
                  <a
                    href={`${API_URL}${result.clean_download_url}`}
                    download
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-emerald-500/20 border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/30 rounded-sm text-[10px] font-bold"
                    data-testid="derive-download-btn"
                  >
                    <Download className="w-3 h-3" />Download .bin
                  </a>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={reset}
                    className="border-[#27272A] text-zinc-400 hover:bg-[#1A1A1A] rounded-sm text-[10px] h-7 px-2.5"
                    data-testid="derive-reset-btn"
                  >
                    Build another
                  </Button>
                </div>
              </AlertDescription>
            </Alert>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-5 border-t border-[#27272A] bg-[#050505]">
          <p className="text-[10px] text-zinc-600 max-w-sm">
            Datalog immediately after flashing. Hardware tolerances vary — first pull should
            be a short partial-throttle roll, not WOT.
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={onClose}
              className="border-[#27272A] text-zinc-400 hover:bg-[#1A1A1A] rounded-sm text-xs"
              data-testid="derive-cancel-btn"
            >
              Close
            </Button>
            <Button
              onClick={build}
              disabled={!canBuild || building}
              className="bg-gradient-to-r from-[#FF5E1A] to-amber-500 hover:from-[#FF7A3D] hover:to-amber-400 text-white rounded-sm text-xs font-bold shadow-[0_0_16px_rgba(255,94,26,0.35)]"
              data-testid="derive-build-btn"
            >
              {building ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Wrench className="w-3.5 h-3.5 mr-1.5" />}
              {building ? 'Building…' : 'Derive Tune'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
